import { useApplicationStore } from "@/stores/Application";
import { AppError, AppErrorCode } from "@ca.vmedia.tv/domains";
import { linkItems } from "@/components/Header/types/LinkItemsCollection";
import { useRedirectToLogin } from "@/composables/useRedirectToLogin";
import  { type LoginSuccessMutationParams, LoginRequestMutationParams } from "@/stores/Account/types";
import { useLanguagesUtils } from "@/composables/useLanguagesUtils";
import { useAccountStore } from "@/stores/Account";
import { useUserSettingsStore } from "@/stores/UserSettings";
import { useRoute } from 'vue-router';
import { useClientMessagesStore } from "@/stores/ClientMessages";
import { UrlPathUtils } from '@/utils';
import { ScreenName } from "@/common/types/screen-names";
import { useStorageStreamProgress } from "@/composables/player/useStorageStreamProgress/useStorageStreamProgress";

export default defineNuxtPlugin(async () => {
    const { getImageUrlTemplates } = useApplicationStore();

    const { startReceiving } = useClientMessagesStore();


    const {
        LoginWithMacAddress,
        SilentLoginToken,
        SilentLoginOkta,
        LoginSuccess,
        SaveLogin,
    } = useAccountStore();
    const {
        fetchSettings,
    } = useUserSettingsStore();

    const { $domains, $platform, $router, $analytics } = useNuxtApp();
    const currentRoute = useRoute();
    const {featureOktaLoginEnabled, authMethod, mainPage, sessianIDTest, userNameIDTest} = useAppConfig();
    const featureMacAddressLoginEnabled = authMethod.featureMacAddressLoginEnabled;
    const featureRefreshTokenLoginEnabled = authMethod.featureRefreshTokenLoginEnabled;
    const { initLanguage, adjustLanguageSettings } = await useLanguagesUtils();
    
    const { sendStreamProgressToApi } = useStorageStreamProgress();
    sendStreamProgressToApi();

    $domains.lsCacher.deleteExpired();
    $domains.idbCacher.deleteExpired().catch((e) => {
        console.error(e);
    });

    let errorMessage: string | null = null;
    let errorCode: AppErrorCode | null = AppErrorCode.OK;

    initLanguage()

    if (!$platform.isRunAllowed()) {
        $router.beforeEach((to, _from, next) => {
            if (to.path !== linkItems.error.routerPath) { 
                errorCode = AppErrorCode.NOT_SUPPORTED_PLATFORM;
                next(linkItems.error.routerPath + `?errorCode=${errorCode}`); 
            } else {
                next();
            }
        });
        return;
    }

    await getImageUrlTemplates();
    
    let loginSuccess = false;
    const userInfo = await $domains.loadUserInfo();

    if (featureMacAddressLoginEnabled && userInfo.sessionId === null) {
        await LoginWithMacAddress().then((res)=>{
            errorCode = res;
        }).catch((err) => {
            if (err instanceof AppError) {
                errorCode = err?.code;
                errorMessage = err?.message;
            } else {
                errorCode = AppErrorCode.LOGIN_NETWORK_ERROR;
                errorMessage = (err as Error)?.message;
            }
        });
    }

    // try to load user info from the local storage
    const { username, sessionId } = await $domains.loadUserInfo();

    if (sessianIDTest !== null) {
        try {
            console.log('featureRefreshTokenLoginEnabled', featureRefreshTokenLoginEnabled);
            console.log('featureOktaLoginEnabled', featureOktaLoginEnabled);
            
            LoginSuccess({ sessionId: sessianIDTest, UserSystemID: userNameIDTest } as LoginSuccessMutationParams);
            SaveLogin(new LoginRequestMutationParams(userNameIDTest ?? ''));

            
            // auto-login
            if (featureRefreshTokenLoginEnabled) {
                await SilentLoginToken().then((res)=>{
                    errorCode = res;
                });
            } else if (featureOktaLoginEnabled) {
                await SilentLoginOkta().then((res)=>{
                    errorCode = res;
                });
            } else {
                LoginSuccess({ sessionId: sessionId, UserSystemID: username } as LoginSuccessMutationParams);
                SaveLogin(new LoginRequestMutationParams(username ?? ''));
            }
            // auto-login success, exception otherwise
            // load settings
            await fetchSettings();

            await adjustLanguageSettings();
            loginSuccess = errorCode === AppErrorCode.OK ? true : false;
            startReceiving().catch((e) => {
                console.error('[startup] startReceiving failed:', e);
            });
        } catch (err) {
            if (err instanceof AppError) {
                errorCode = err?.code;
                errorMessage = err?.message;
            } else {
                errorCode = AppErrorCode.LOGIN_NETWORK_ERROR;
                errorMessage = (err as Error)?.message;
            }
        }
    } else {
      
    }
   
    if ($analytics) {
        $analytics.createOpenAppEvent(ScreenName.SPLASH_SCREEN, ScreenName.SPLASH_SCREEN);
    }

    if (currentRoute.path === linkItems.root.routerPath) {
        setTimeout(() => {
            if (errorCode == AppErrorCode.OK) {
                if (loginSuccess) {
                    void $router.push(linkItems[mainPage].routerPath);
                } else {
                    useRedirectToLogin();
                }
            } else {
                let url = UrlPathUtils.buildUrl(linkItems.error.routerPath, {errorCode, errorMessage});
                switch (errorCode) {
                    case AppErrorCode.LOGIN_TOKEN_ERROR:
                            url = linkItems.login.routerPath;
                        break;
                }
                void $router.push(url);
            }
        }, 100);
    }
});
